import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  getClientMessages, markClientMessagesRead, postProjectComment, getMentionableUsers,
  uploadFile, API_BASE, markClientHandled,
} from "../lib/api";
import { Card } from "./ui/card";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { renderRichBody as renderMentionedBody } from "../lib/richText";
import { RichTextarea } from "./RichTextarea";
import {
  Loader2, Send, Search as SearchIcon, MessageSquare,
  Globe, Megaphone, Target, ExternalLink, Paperclip, FileText, X as XIcon, Lock,
  Check, CheckCircle2,
  CornerUpLeft,
} from "lucide-react";

// Internal roles that can see the ✓ Handled pill / Internal-only comments.
const INTERNAL_ROLES = new Set(["super_admin", "manager", "ops_coordinator"]);

const PRODUCT_LABEL = {
  marketing: "Marketing",
  web_design: "Web Design",
  seo_aeo: "SEO/AEO",
  prospect_tools: "ProspectID",
};
const PRODUCT_COLOR = {
  marketing: "bg-rose-100 text-rose-800 border-rose-200",
  web_design: "bg-blue-100 text-blue-800 border-blue-200",
  seo_aeo: "bg-emerald-100 text-emerald-800 border-emerald-200",
  prospect_tools: "bg-purple-100 text-purple-800 border-purple-200",
};
const PRODUCT_ICON = { marketing: Megaphone, web_design: Globe, seo_aeo: SearchIcon, prospect_tools: Target };

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function getInitials(name, override) {
  if (override) return override;
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

// Regex: matches http(s):// links up to the next whitespace. Includes the
// full path/query so Drive/Docs/Loom URLs stay clickable in one piece.
// Trailing sentence punctuation is peeled off after the match.

function ProductChip({ product, onClick, active }) {
  if (!product) return null;
  const Icon = PRODUCT_ICON[product] || MessageSquare;
  const className = `inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${PRODUCT_COLOR[product] || "bg-zinc-100"} ${active ? "ring-2 ring-offset-1 ring-primary" : ""}`;
  const content = (
    <>
      <Icon className="h-3 w-3" />
      {PRODUCT_LABEL[product] || product}
    </>
  );
  if (!onClick) {
    return <span className={className} data-testid={`unified-product-chip-${product}`}>{content}</span>;
  }
  return (
    <button type="button" onClick={onClick} className={className} data-testid={`unified-product-chip-${product}`}>
      {content}
    </button>
  );
}

/**
 * UnifiedClientConversation
 * Renders a single client's unified conversation thread across ALL of that client's
 * orders (projects) + change requests, with each message tagged by product type.
 *
 * Props:
 *   clientId (string, required)   – the client whose thread to load
 *   currentOrderId (string)       – optional: the order being viewed. When set,
 *                                    messages from this order get a subtle highlight,
 *                                    the composer defaults to this order, and a
 *                                    "Also in conversation" strip shows sibling orders.
 *   title (string)                – header title (default: "Client Conversation")
 *   heightClass (string)          – Tailwind height class for the outer card
 *                                    (default: "h-[600px]")
 *   hideViewOrderBtn (bool)       – hide the top-right "View Order" button
 *                                    (useful when embedded inside an order page)
 */
export default function UnifiedClientConversation({
  clientId,
  currentOrderId = null,
  title = "Client Conversation",
  heightClass = "h-[600px]",
  hideViewOrderBtn = false,
  // Feb 2026 — ProjectDetailPage top working area ships with newest-first so
  // reps don't have to scroll past weeks of chatter to see today's reply.
  newestFirst = false,
  // Highlight external (rep/client) messages that haven't been replied-to by
  // internal staff within this many hours as "stale". 0 disables the pill.
  staleThresholdHours = 0,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [threadData, setThreadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [productFilter, setProductFilter] = useState("all");
  // When viewing from an order page (currentOrderId set), default to showing
  // ONLY messages from the current order. Prior orders' chatter stays available
  // via the "Show earlier orders" toggle so nothing is lost — new orders just
  // open with a fresh canvas. User-requested, Feb 2026.
  const [showEarlierOrders, setShowEarlierOrders] = useState(false);
  useEffect(() => {
    // Reset to "current only" every time the order under view changes.
    setShowEarlierOrders(false);
  }, [currentOrderId]);
  // In-thread text search — type to highlight matches. Rep-friendly: no modal, no modes.
  const [threadSearch, setThreadSearch] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replyThreadId, setReplyThreadId] = useState("");
  const [sending, setSending] = useState(false);
  // Internal-only toggle — when on, the comment is hidden from reps + external
  // roles. Auto-flips on when the compose text contains an @internal-mention.
  const [internalOnly, setInternalOnly] = useState(false);
  const [mentionableUsers, setMentionableUsers] = useState([]);
  const [mentionState, setMentionState] = useState({ open: false, query: "", caret: 0 });
  const [pendingAttachments, setPendingAttachments] = useState([]);  // [{id, filename}] for the next reply
  const [uploading, setUploading] = useState(false);
  const [handling, setHandling] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const focusReply = () => {
    const el = textareaRef.current;
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    el?.focus();
  };
  const fileInputRef = useRef(null);

  useEffect(() => {
    getMentionableUsers().then((r) => setMentionableUsers(r.data || [])).catch(() => setMentionableUsers([]));
  }, []);

  const fetchThread = async (opts = { scroll: true }) => {
    if (!clientId) return;
    try {
      const r = await getClientMessages(clientId);
      setThreadData(r.data);
      // Default reply thread: current order if provided and present in threads, else first
      const threads = r.data?.threads || [];
      if (currentOrderId && threads.some((t) => t.thread_id === currentOrderId)) {
        setReplyThreadId(currentOrderId);
      } else if (threads[0]) {
        setReplyThreadId(threads[0].thread_id);
      }
      if (opts.scroll) {
        setTimeout(() => {
          if (newestFirst) {
            // Newest-first: scroll to the TOP of the message pane so the
            // reader lands on today's reply, not on week-old chatter.
            messagesEndRef.current?.parentElement?.scrollTo?.({ top: 0, behavior: "smooth" });
          } else {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
          }
        }, 200);
      }
      markClientMessagesRead(clientId).catch(() => {});
    } catch {
      setThreadData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchThread({ scroll: true });
  }, [clientId, currentOrderId]);

  // Pre-compute the "stale" flag per external message (rep/client authored,
  // no newer internal reply, older than staleThresholdHours). We walk the
  // messages in chronological order once to know whether an internal reply
  // ever came after each external message.
  const staleMessageIds = useMemo(() => {
    if (!threadData || !staleThresholdHours) return new Set();
    const INTERNAL = new Set(["super_admin", "manager", "ops_coordinator"]);
    const chronological = [...(threadData.messages || [])].sort(
      (a, b) => (a.created_at || "").localeCompare(b.created_at || "")
    );
    // Find for each external message whether any later message is internal.
    const stale = new Set();
    const threshold = staleThresholdHours * 3600 * 1000;
    const now = Date.now();
    for (let i = 0; i < chronological.length; i += 1) {
      const m = chronological[i];
      if (INTERNAL.has(m.author_role) || m.internal_only) continue;
      // Was there a later internal reply?
      let replied = false;
      for (let j = i + 1; j < chronological.length; j += 1) {
        if (INTERNAL.has(chronological[j].author_role) && !chronological[j].internal_only) {
          replied = true;
          break;
        }
      }
      if (replied) continue;
      // Age gate
      const ts = m.created_at ? new Date(m.created_at).getTime() : 0;
      if (!ts || (now - ts) < threshold) continue;
      // Use a stable identifier — message id if present, else timestamp+author
      stale.add(m.id || `${m.author_id}:${m.created_at}`);
    }
    return stale;
  }, [threadData, staleThresholdHours]);

  // Partner messages still waiting on a public RP reply (and not marked handled) — never hidden.
  const awaitingReplyKeys = useMemo(() => {
    if (!threadData) return new Set();
    const INTERNAL = new Set(["super_admin", "manager", "ops_coordinator"]);
    const handledAt = threadData.is_handled ? threadData.last_handled_at || "" : "";
    const chron = [...(threadData.messages || [])].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    const out = new Set();
    let repliedAfter = false;
    for (let i = chron.length - 1; i >= 0; i -= 1) {
      const m = chron[i];
      if (INTERNAL.has(m.author_role)) { if (!m.internal_only) repliedAfter = true; continue; }
      if (m.internal_only || repliedAfter) continue;
      if (handledAt && (m.created_at || "") <= handledAt) continue;
      out.add(m.id || `${m.author_id}:${m.created_at}`);
    }
    return out;
  }, [threadData]);
  const isHiddenAsClosed = (m) =>
    m.project_id !== currentOrderId && m.project_closed && !awaitingReplyKeys.has(m.id || `${m.author_id}:${m.created_at}`);

  const filteredMessages = useMemo(() => {
    if (!threadData) return [];
    let base = productFilter === "all"
      ? threadData.messages
      : threadData.messages.filter((m) => m.product_type === productFilter);
    // From an order page, hide chatter from CLOSED (completed/archived) orders unless expanded.
    // Other open orders and anything still awaiting a reply always stay visible.
    if (currentOrderId && !showEarlierOrders) {
      base = base.filter((m) => !isHiddenAsClosed(m));
    }
    // In-thread text search — case-insensitive substring over message body.
    const q = threadSearch.trim().toLowerCase();
    if (q) {
      base = base.filter((m) => (m.body || "").toLowerCase().includes(q));
    }
    if (!newestFirst) return base;
    // Clone before sort — threadData is from state, don't mutate.
    return [...base].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [threadData, productFilter, newestFirst, threadSearch, currentOrderId, showEarlierOrders, awaitingReplyKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  // How many messages come from closed orders (and are hideable)?
  const earlierOrdersCount = useMemo(() => {
    if (!threadData || !currentOrderId) return 0;
    return (threadData.messages || []).filter(isHiddenAsClosed).length;
  }, [threadData, currentOrderId, awaitingReplyKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sibling orders (other orders on this client) — used by the "Also in conversation" strip
  const siblingThreads = useMemo(() => {
    if (!threadData || !currentOrderId) return [];
    return (threadData.threads || []).filter((t) => t.thread_id !== currentOrderId);
  }, [threadData, currentOrderId]);

  // Auto-flip Internal-only on as soon as the compose text contains a
  // resolvable @mention of an internal teammate. Only applies when the
  // AUTHOR is internal — a rep @mentioning Dan stays public (Slack semantics).
  const [internalOnlyLocked, setInternalOnlyLocked] = useState(false);
  const viewerIsInternalRole = INTERNAL_ROLES.has(user?.role);
  useEffect(() => {
    if (!viewerIsInternalRole) {
      // Reps + clients can never post internal-only; keep the flag off.
      if (internalOnly) setInternalOnly(false);
      return;
    }
    const internalMatches = (replyText.match(/@([A-Za-z][A-Za-z0-9\-_.']{0,30})/g) || [])
      .map((t) => t.slice(1).toLowerCase());
    const anyInternal = internalMatches.some((tok) =>
      mentionableUsers.some(
        (u) =>
          ["super_admin", "manager", "ops_coordinator"].includes(u.role) &&
          (u.name || "").toLowerCase().startsWith(tok)
      )
    );
    if (anyInternal && !internalOnlyLocked) setInternalOnly(true);
    if (!anyInternal && !internalOnlyLocked) setInternalOnly(false);
  }, [replyText, mentionableUsers, internalOnlyLocked, viewerIsInternalRole]);

  const handleSend = async () => {
    if ((!replyText.trim() && pendingAttachments.length === 0) || !replyThreadId) return;
    setSending(true);
    try {
      const fileIds = pendingAttachments.map((f) => f.id);
      await postProjectComment(replyThreadId, replyText.trim(), fileIds, internalOnly);
      setReplyText("");
      setPendingAttachments([]);
      setInternalOnly(false);
      setInternalOnlyLocked(false);
      await fetchThread({ scroll: true });
      toast.success(internalOnly ? "Internal note posted" : "Message sent");
    } catch {
      toast.error("Could not send. Try again.");
    } finally {
      setSending(false);
    }
  };

  const handleAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 25 MB — skipped`);
          continue;
        }
        const { data } = await uploadFile(file);
        setPendingAttachments((prev) => [...prev, { id: data.id, filename: data.filename }]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePendingAttachment = (id) => {
    setPendingAttachments((prev) => prev.filter((f) => f.id !== id));
  };

  const handleMarkHandled = async () => {
    if (!clientId) return;
    setHandling(true);
    try {
      await markClientHandled(clientId);
      // Optimistic local flip so the pill switches to "Handled" without a refetch.
      setThreadData((prev) => (prev ? { ...prev, is_handled: true } : prev));
      toast.success("Marked as handled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark as handled");
    } finally {
      setHandling(false);
    }
  };

  const onReplyChange = (e) => {
    const value = e.target.value;
    setReplyText(value);
    const caret = e.target.selectionStart || value.length;
    const upToCaret = value.slice(0, caret);
    const match = upToCaret.match(/(^|\s)@([A-Za-z][A-Za-z0-9\-_.']{0,30})$/);
    if (match) {
      setMentionState({ open: true, query: match[2].toLowerCase(), caret });
    } else {
      setMentionState((s) => (s.open ? { ...s, open: false } : s));
    }
  };

  const insertMention = (mUser) => {
    const value = replyText;
    const caret = mentionState.caret;
    const upToCaret = value.slice(0, caret);
    const newUpTo = upToCaret.replace(/@([A-Za-z][A-Za-z0-9\-_.']{0,30})$/, `@${(mUser.name || "").split(" ")[0]} `);
    const next = newUpTo + value.slice(caret);
    setReplyText(next);
    setMentionState({ open: false, query: "", caret: newUpTo.length });
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newUpTo.length, newUpTo.length);
      }
    }, 0);
  };

  const filteredMentionables = useMemo(() => {
    if (!mentionState.open) return [];
    const q = mentionState.query;
    return mentionableUsers
      .filter((u) => (u.name || "").toLowerCase().includes(q))
      .slice(0, 6);
  }, [mentionState, mentionableUsers]);

  if (loading) {
    return (
      <Card className={`border flex items-center justify-center ${heightClass}`} data-testid="unified-conv-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </Card>
    );
  }
  if (!threadData) {
    return (
      <Card className={`border flex items-center justify-center text-sm text-muted-foreground ${heightClass}`}>
        Could not load this conversation.
      </Card>
    );
  }

  return (
    <Card className={`border flex flex-col overflow-hidden ${heightClass}`} data-testid="unified-client-conversation">
      {/* Header */}
      <div className="p-3 border-b shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="min-w-0">
            <div className="font-semibold truncate flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              {title} — {threadData.client_name}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {currentOrderId
                ? (showEarlierOrders
                    ? "Showing this order plus closed orders for this client."
                    : "Showing this order and the client’s other open orders. Closed orders are one click away.")
                : "Unified thread across all orders for this client. Each message is tagged by product."}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {!hideViewOrderBtn && threadData.primary_project_id && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate(`/orders/${threadData.primary_project_id}`)}
                className="h-7 text-xs"
                data-testid="unified-view-order"
              >
                <ExternalLink className="mr-1 h-3 w-3" /> View Order
              </Button>
            )}
            <button
              type="button"
              onClick={() => setProductFilter("all")}
              className={`text-xs px-2 py-0.5 rounded-full border ${productFilter === "all" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}
              data-testid="unified-product-filter-all"
            >All</button>
            {(threadData.active_products || []).map((p) => (
              <ProductChip key={p} product={p} onClick={() => setProductFilter(p)} active={productFilter === p} />
            ))}
            {/* In-thread text search — type to filter visible messages. */}
            <div className="relative ml-2" data-testid="unified-thread-search">
              <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <input
                type="text"
                value={threadSearch}
                onChange={(e) => setThreadSearch(e.target.value)}
                placeholder="Search in thread…"
                className="h-7 pl-7 pr-2 text-xs rounded-full border border-zinc-200 bg-white focus:outline-none focus:border-primary w-44"
                data-testid="unified-thread-search-input"
              />
            </div>
          </div>
        </div>

        {/* "Also in conversation" — sibling orders when viewing from an order page */}
        {currentOrderId && siblingThreads.length > 0 && (
          <div className="mt-2 pt-2 border-t flex items-center gap-2 flex-wrap" data-testid="also-in-conversation">
            <span className="text-[11px] font-medium text-muted-foreground">Also in this conversation:</span>
            {siblingThreads.map((t) => (
              <button
                key={t.thread_id}
                type="button"
                onClick={() => navigate(`/orders/${t.thread_id}`)}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium hover:opacity-80 transition ${t.product_type ? PRODUCT_COLOR[t.product_type] : "bg-zinc-100 text-zinc-700 border-zinc-200"}`}
                title={`Open ${t.name}`}
                data-testid={`sibling-order-${t.thread_id}`}
              >
                {t.product_type ? PRODUCT_LABEL[t.product_type] : t.name}
                <ExternalLink className="h-2.5 w-2.5" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Reply composer */}
      <div className="border-b p-3 shrink-0 bg-white relative" data-testid="reply-composer-top">
        {mentionState.open && filteredMentionables.length > 0 && (
          <div
            className="absolute top-full left-3 mt-1 w-64 max-h-56 overflow-y-auto bg-white border border-zinc-200 rounded-lg shadow-lg z-50"
            data-testid="unified-mention-picker"
          >
            {filteredMentionables.map((mu) => (
              <button
                type="button"
                key={mu.id}
                onMouseDown={(e) => { e.preventDefault(); insertMention(mu); }}
                className="w-full text-left px-3 py-2 hover:bg-indigo-50 flex items-center gap-2 text-sm"
                data-testid={`unified-mention-option-${mu.id}`}
              >
                <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold">
                  {getInitials(mu.name, mu.display_initials)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{mu.name}</div>
                  <div className="text-[10px] text-muted-foreground">{mu.role?.replace("_", " ")}</div>
                </div>
              </button>
            ))}
          </div>
        )}
        {/* Pending attachments strip — shown before send */}
        {pendingAttachments.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5" data-testid="pending-attachments">
            {pendingAttachments.map((f) => (
              <span
                key={f.id}
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-secondary text-[11px] font-medium border"
                data-testid={`pending-attachment-${f.id}`}
              >
                <FileText className="h-3 w-3" />
                <span className="truncate max-w-[180px]" title={f.filename}>{f.filename}</span>
                <button
                  type="button"
                  onClick={() => removePendingAttachment(f.id)}
                  className="text-muted-foreground hover:text-destructive"
                  title="Remove attachment"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        {/* Internal-only toggle — private note visible to super_admin /
            manager / ops_coordinator only. Auto-flips on when the compose
            text @-mentions an internal teammate; user can override via
            the chip. Hidden from reps + clients — they can @mention internal
            teammates but their message always stays public. */}
        {viewerIsInternalRole && (
        <div className="mb-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              const next = !internalOnly;
              setInternalOnly(next);
              setInternalOnlyLocked(true);  // stop auto-toggle for this compose
            }}
            data-testid="unified-internal-toggle"
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${
              internalOnly
                ? "bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200"
                : "bg-zinc-50 text-zinc-600 border-zinc-200 hover:bg-zinc-100"
            }`}
            title={internalOnly
              ? "This message will be visible to internal staff only — the rep will not see it."
              : "Post as a public message. Click to make it internal-only."}
          >
            <Lock className="h-3 w-3" />
            {internalOnly ? "Internal only" : "Post publicly"}
          </button>
          {internalOnly && (
            <span className="text-[10px] text-amber-700">
              Rep + client will NOT see this comment
            </span>
          )}
        </div>
        )}
        <div className="flex flex-wrap sm:flex-nowrap items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleAttach}
            data-testid="unified-file-input"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            title="Attach file(s) — 25 MB max each"
            data-testid="unified-attach-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </Button>
          <RichTextarea
            ref={textareaRef}
            value={replyText}
            onChange={onReplyChange}
            placeholder="Type your reply... (use @name to ping a teammate)"
            rows={3}
            wrapperClassName="flex-1 min-w-0"
            className="w-full min-h-[84px] resize-y"
            data-testid="unified-reply-textarea"
            onKeyDown={(e) => {
              if (e.key === "Escape" && mentionState.open) {
                setMentionState((s) => ({ ...s, open: false }));
                return;
              }
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <div className="flex sm:flex-col gap-1.5 shrink-0 w-full sm:w-auto justify-end">
            <Select value={replyThreadId} onValueChange={setReplyThreadId}>
              <SelectTrigger className="h-9 text-xs w-[160px]" data-testid="unified-reply-thread-select">
                <SelectValue placeholder="Tag to order..." />
              </SelectTrigger>
              <SelectContent>
                {(threadData.threads || []).map((t) => {
                  const isCurrent = t.thread_id === currentOrderId;
                  const label = t.product_type ? PRODUCT_LABEL[t.product_type] : t.name;
                  return (
                    <SelectItem key={t.thread_id} value={t.thread_id} className="text-xs">
                      {label}{isCurrent ? " (this order)" : ""}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Button
              onClick={handleSend}
              disabled={sending || (!replyText.trim() && pendingAttachments.length === 0) || !replyThreadId}
              size="sm"
              data-testid="unified-reply-send-btn"
            >
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (<><Send className="h-3.5 w-3.5 mr-1" /> Send</>)}
            </Button>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">
          Tip: Ctrl/⌘+Enter to send · Ctrl/⌘+B or the B button for bold · @name to ping a teammate · 📎 to attach a report · Pick which order this reply belongs to before sending
        </p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-secondary/20">
        {currentOrderId && earlierOrdersCount > 0 && (
          <div
            className="flex items-center justify-between gap-2 -mt-1 mb-2 px-3 py-1.5 rounded-md border border-dashed border-amber-300 bg-amber-50/60 text-amber-900"
            data-testid="earlier-orders-toggle"
          >
            <div className="text-[11px]">
              {showEarlierOrders
                ? <>Showing <b>{earlierOrdersCount}</b> message{earlierOrdersCount === 1 ? "" : "s"} from closed orders.</>
                : <><b>{earlierOrdersCount}</b> message{earlierOrdersCount === 1 ? "" : "s"} from completed / archived orders — hidden to keep this thread clean. Open orders and anything awaiting a reply always show.</>}
            </div>
            <button
              type="button"
              onClick={() => setShowEarlierOrders((v) => !v)}
              className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white transition"
              data-testid="earlier-orders-toggle-btn"
            >
              {showEarlierOrders ? "Hide closed orders" : "Show closed orders"}
            </button>
          </div>
        )}
        {filteredMessages.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-10">
            {currentOrderId && !showEarlierOrders && earlierOrdersCount > 0
              ? "No messages on this order yet. Start one above."
              : "No messages yet. Start one above."}
          </div>
        ) : filteredMessages.map((m, idx) => {
          const mine = m.author_id === user?.id;
          const isCurrentOrder = currentOrderId && m.project_id === currentOrderId;
          const authorIsInternal = INTERNAL_ROLES.has(m.author_role);
          const isExternal = !mine && !authorIsInternal;
          const viewerIsInternal = INTERNAL_ROLES.has(user?.role);
          // Mirror the MessagesPage per-message Handled pill: external (rep/client)
          // messages show a ✓ Handled action for internal viewers, flipping to a
          // passive "Handled" badge once the thread has been marked handled.
          const showHandledPill = isExternal && viewerIsInternal;
          const alreadyHandled = !!threadData?.is_handled;
          // Stale indicator: external messages with no internal reply AND older
          // than the configured threshold render a red left-border + "Needs reply · Xd" pill.
          const staleKey = m.id || `${m.author_id}:${m.created_at}`;
          const isStale = staleMessageIds.has(staleKey);
          const staleAgeLabel = (() => {
            if (!isStale || !m.created_at) return "";
            const ms = Date.now() - new Date(m.created_at).getTime();
            const d = Math.floor(ms / 86400000);
            if (d >= 1) return `${d}d`;
            const h = Math.floor(ms / 3600000);
            return `${h}h`;
          })();
          return (
            <div key={idx} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[78%] rounded-lg px-3 py-2 border ${mine ? "bg-primary text-primary-foreground border-primary/30" : "bg-white"} ${m.internal_only ? "ring-2 ring-amber-400 border-amber-400" : ""} ${isStale ? "border-l-4 border-l-red-500 bg-red-50" : ""} ${isCurrentOrder && !mine && !m.internal_only ? "ring-2 ring-amber-300 ring-offset-1" : ""} ${isCurrentOrder && mine && !m.internal_only ? "ring-2 ring-amber-200" : ""}`}
                data-testid={isCurrentOrder ? `msg-current-order-${idx}` : `msg-${idx}`}
              >
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-[11px] font-medium ${mine ? "text-primary-foreground/80" : "text-foreground"}`}>{m.author_name || "User"}</span>
                  {m.internal_only && (
                    <span
                      className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase bg-amber-500 text-white rounded-full px-1.5 py-0.5"
                      title="Internal only — visible to super admins, managers, and ops staff. Reps and clients will not see it."
                      data-testid={`msg-internal-badge-${idx}`}
                    >
                      <Lock className="h-2.5 w-2.5" /> Internal
                    </span>
                  )}
                  {m.product_type && (
                    <Badge variant="outline" className={`${PRODUCT_COLOR[m.product_type]} text-[10px] py-0 px-1.5 h-4`}>
                      {PRODUCT_LABEL[m.product_type]}
                    </Badge>
                  )}
                  {isCurrentOrder && (
                    <span
                      className={`text-[9px] px-1.5 py-0 rounded-full font-semibold ${mine ? "bg-amber-200 text-amber-900" : "bg-amber-100 text-amber-800 border border-amber-200"}`}
                      title="This message is on the order you're currently viewing"
                    >
                      This order
                    </span>
                  )}
                  <span className={`text-[10px] ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{timeAgo(m.created_at)}</span>
                  {!mine && (
                    <button type="button" onClick={focusReply}
                      className="text-[10px] font-semibold text-primary hover:underline inline-flex items-center gap-0.5"
                      data-testid={`msg-reply-btn-${idx}`}>
                      <CornerUpLeft className="h-3 w-3" /> Reply
                    </button>
                  )}
                  {isStale && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 bg-red-100 border border-red-300 rounded-full px-1.5 py-0.5"
                      data-testid={`msg-stale-pill-${idx}`}
                      title={`No internal reply after ${staleAgeLabel} — this message is overdue.`}
                    >
                      Needs reply · {staleAgeLabel}
                    </span>
                  )}
                  {showHandledPill && (
                    alreadyHandled ? (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5"
                        data-testid={`unified-msg-handled-badge-${idx}`}
                        title="This thread is already marked handled — a new reply from the rep will bring it back to Reply Needed."
                      >
                        <Check className="h-3 w-3" /> Handled
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleMarkHandled(); }}
                        disabled={handling}
                        data-testid={`unified-msg-handled-btn-${idx}`}
                        title="Mark this thread handled — clears it from Reply Needed until the next external reply."
                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 rounded-full px-1.5 py-0.5 transition disabled:opacity-60"
                      >
                        <CheckCircle2 className="h-3 w-3" /> Handled?
                      </button>
                    )
                  )}
                </div>
                <div className="text-sm whitespace-pre-wrap leading-snug">{renderMentionedBody(m.body)}</div>
                {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                  <div className={`mt-2 pt-2 border-t space-y-1 ${mine ? "border-primary-foreground/25" : "border-zinc-200"}`}>
                    {m.attachments.map((att) => (
                      <a
                        key={att.id}
                        href={`${API_BASE}/api/files/${att.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-1.5 text-[11px] font-medium truncate hover:underline ${mine ? "text-primary-foreground" : "text-primary"}`}
                        data-testid={`msg-attachment-${att.id}`}
                        title={`Download ${att.filename}`}
                      >
                        <Paperclip className="h-3 w-3 shrink-0" />
                        <span className="truncate">{att.filename}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

    </Card>
  );
}
